import { useEffect, useState } from "react";

const CHARS_PER_TICK = 2;
const TICK_MS = 18;

// Summaries already typed out this session, so reopening the modal shows the
// text at once instead of replaying the animation.
const typedKeys = new Set<string>();

const prefersReducedMotion = () =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

const shouldSkip = (key: string) =>
  typedKeys.has(key) || prefersReducedMotion();

/**
 * Reveals `text` a few characters at a time, as if it were being typed, the
 * first time a given `key` is shown. Returns the visible slice and whether
 * typing is still in progress.
 */
export const useTypewriter = (text: string, key: string) => {
  const [visibleLength, setVisibleLength] = useState(() =>
    shouldSkip(key) ? text.length : 0
  );

  useEffect(() => {
    if (shouldSkip(key)) {
      setVisibleLength(text.length);
      return;
    }

    let length = 0;
    setVisibleLength(length);

    const interval = window.setInterval(() => {
      length = Math.min(length + CHARS_PER_TICK, text.length);
      setVisibleLength(length);

      if (length >= text.length) {
        window.clearInterval(interval);
        typedKeys.add(key);
      }
    }, TICK_MS);

    return () => window.clearInterval(interval);
  }, [text, key]);

  return {
    text: text.slice(0, visibleLength),
    isTyping: visibleLength < text.length
  };
};
